import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { History, Loader2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import ModuleAccessDenied from '../../components/ui/ModuleAccessDenied';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import contractService from '../../services/contracts';
import { Fingerprint } from '../../components/cultivar/contracts/ContractBits';
import { formatDateTimeInTz, useOrgTimezone } from '../../utils/timezone';
import { CONTRACT_EVENT_LABELS, type ContractEvent } from '../../types/contracts';

/**
 * Everything that happened to this organisation's contracts, newest first:
 * drafts, publications, agreements sent, signed, declined and withdrawn,
 * programme and group changes, and sublicensing ticks. Append-only.
 */
export default function ContractsHistory() {
  const orgTz = useOrgTimezone();
  const { hasAccess, loading } = useModuleLicense('cultivar', ['pbr', 'admin']);
  const [events, setEvents] = useState<ContractEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!hasAccess) return;
    contractService.orgEvents(500)
      .then(res => setEvents(res.success ? (res.data ?? []) : []))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load the history'));
  }, [hasAccess]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary-600" /></div>;
  if (!hasAccess) return <ModuleAccessDenied moduleName="Cultivar (pbr or admin seat)" />;

  const linkFor = (e: ContractEvent) => {
    if (e.license_id) return { to: `/cultivar/licences/${e.license_id}/agreement`, label: `Agreement #${e.license_id}` };
    if (e.family_id) return { to: `/cultivar/contracts/${e.family_id}`, label: 'Contract' };
    if (e.order_id) return { to: `/cultivar/orders/${e.order_id}`, label: `Order #${e.order_id}` };
    if (e.program_id) return { to: `/cultivar/admin/programs/${e.program_id}/contract-templates`, label: 'Programme' };
    if (e.sublicence_group_id) return { to: '/cultivar/sublicence-groups', label: 'Group' };
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader title="Contract history" subtitle="A permanent record — nothing here can be edited or deleted" icon={<History className="h-6 w-6 text-white" />} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Card>
          <CardContent className="p-4">
            {error ? (
              <p className="text-sm text-red-700">{error}</p>
            ) : events === null ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary-600" /></div>
            ) : events.length === 0 ? (
              <p className="text-sm text-neutral-500">Nothing recorded yet.</p>
            ) : (
              <ol className="space-y-3">
                {events.map(e => {
                  const link = linkFor(e);
                  return (
                    <li key={e.id} className="border-l-2 border-neutral-200 pl-3 min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="text-sm font-medium text-neutral-900">{CONTRACT_EVENT_LABELS[e.action] || e.action}</span>
                        <span className="text-xs text-neutral-500">{formatDateTimeInTz(e.created_at, orgTz)}</span>
                        {e.actor_name && <span className="text-xs text-neutral-500">· {e.actor_name}</span>}
                        {link && <Link to={link.to} className="text-xs text-primary-600 hover:underline">{link.label}</Link>}
                      </div>
                      {e.summary && <p className="text-sm text-neutral-700 break-words">{e.summary}</p>}
                      {e.content_hash && <Fingerprint hash={e.content_hash} />}
                    </li>
                  );
                })}
              </ol>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
